import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { setKPI, selectIsEnquiring, selectKPI } from './kpiSlice';
import { createKPI, getKPI, saveKpiGroup } from './kpiThunk';
import { Layout, Menu, Spin, theme, Button, Modal, Input, Form, Dropdown } from 'antd';
import { PlusOutlined, EditOutlined, FormOutlined, SaveOutlined, DeleteOutlined, DownOutlined } from '@ant-design/icons';
import { KPICategory, KPIItem } from './kpiDataTypes';
import { v4 as uuidv4 } from 'uuid';
import KpiForm from './kpiForm';
import { useSearchParams } from 'react-router-dom';

const { Header, Content, Sider } = Layout;

const KPI = () => {
    const dispatch = useAppDispatch();
    const isEnquiring = useAppSelector(selectIsEnquiring);
    const kpiData = useAppSelector(selectKPI) as KPICategory[];

    const [categories, setCategories] = useState<KPICategory[]>([]);
    const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
    const [selectedKPIId, setSelectedKPIId] = useState<string | null>(null);
    const [categoryModalOpen, setCategoryModalOpen] = useState(false);
    const [categoryEditId, setCategoryEditId] = useState<string | null>(null);
    const [categoryName, setCategoryName] = useState('');
    const [categoryDescription, setCategoryDescription] = useState('');
    const [kpiModalOpen, setKPIModalOpen] = useState(false);
    const [kpiEditId, setKPIEditId] = useState<string | null>(null);
    const [kpiName, setKPIName] = useState('');

    let [query, setQueryParams] = useSearchParams();
    
    const {
        token: { colorBgContainer, borderRadiusLG },
    } = theme.useToken();

    useEffect(() => {
        dispatch(getKPI());
    }, [dispatch]);

    useEffect(() => {
        const groupId = query.get('groupId');
        const kpiId = query.get('kpiId');
        if (groupId) {
            setSelectedCategoryId(groupId);
        }
        if (kpiId) {
            setSelectedKPIId(kpiId);
        }
    }, [query]);

    useEffect(() => {
        setCategories(kpiData || []);

    }, [kpiData]);

    // Category handlers
    const handleCategorySelect = (id: string) => {
        query.set('groupId', id);
        setQueryParams(query);
    };
    const handleCategoryAdd = () => {
        setCategoryEditId(null);
        setCategoryName('');
        setCategoryModalOpen(true);
    };
    const handleCategoryEdit = (id: string, name: string) => {
        setCategoryEditId(id);
        setCategoryName(name);
        setCategoryModalOpen(true);
    };
    const handleCategorySave = () => {
        if (categoryEditId) {
            setCategories(categories.map(cat => cat.id === categoryEditId ? { ...cat, name: categoryName } : cat));
            setCategoryEditId(null);
        } else {
            dispatch(saveKpiGroup({ title: categoryName, description: categoryDescription }))
            .then(() => {
                setCategoryModalOpen(false);
                setCategoryName('');
                dispatch(getKPI());
            });
        }
    };
    const handleCategoryDelete = (id: string) => {
        setCategories(categories.filter(cat => cat.id !== id));
        if (selectedCategoryId === id) handleCategorySelect('');
    };

    // KPI handlers
    const handleKPIAdd = () => {
        setKPIEditId(null);
        setKPIName('');
        setKPIModalOpen(true);
    };

    const handleKPISave = () => {
        dispatch(createKPI({ title: kpiName, group: selectedCategoryId }))
        .then((resp) => {
            setKPIModalOpen(false);
            setKPIName('');
            dispatch(getKPI());
            handleKPISelect(resp?.payload?._id);
        });
    };

    const handleKPISelect = (id: string) => {
        query.set('kpiId', id);
        setQueryParams(query);
    };

    // Render helpers
    const selectedCategory = categories.find(cat => cat.id === selectedCategoryId);
    const selectedKPI = selectedCategory?.kpis.find(kpi => kpi._id === selectedKPIId);

    return (
        <Layout style={{ height: '100%', background: colorBgContainer, borderRadius: borderRadiusLG }}>
            <Sider width={300} style={{ background: colorBgContainer }}>
                <Menu
                    mode="inline"
                    selectedKeys={[selectedCategoryId || '', selectedKPIId || '']}
                    openKeys={[selectedCategoryId || '']}
                    style={{ height: '100%' }}
                    onClick={({ keyPath }) => {
                        if (keyPath[0] && keyPath[0].startsWith('add-kpi-')) return;
                        if (keyPath[1] === 'new-category') return;
                        handleKPISelect(keyPath[0] || '');
                        handleCategorySelect(keyPath[1] || '');
                    }}
                >
                    <Menu.Item key="new-category" icon={<PlusOutlined />} onClick={handleCategoryAdd}>
                        Create KPI Group
                    </Menu.Item>
                    {categories.map(category => (
                        <Menu.SubMenu
                            key={category.id}
                            title={
                                <span>
                                    {category.title}
                                    <Button
                                        type="dashed"
                                        disabled
                                        icon={<EditOutlined />}
                                        size="small"
                                        style={{ marginLeft: 8 }}
                                        onClick={e => { e.stopPropagation(); handleCategoryEdit(category.id, category.title); }}
                                    />
                                    <Button
                                        type="dashed"
                                        disabled
                                        icon={<DeleteOutlined />}
                                        size="small"
                                        danger
                                        style={{ marginLeft: 4 }}
                                        onClick={e => { e.stopPropagation(); handleCategoryDelete(category.id); }}
                                    />
                                </span>
                            }
                            onTitleClick={() => handleCategorySelect(category.id)}
                        >
                            <Menu.Item key={`add-kpi-${category.id}`} icon={<PlusOutlined />} onClick={e => { e.domEvent.stopPropagation(); handleCategorySelect(category.id); handleKPIAdd(); }}>
                                Add KPI
                            </Menu.Item>
                            {category.kpis.map(kpi => (
                                <Menu.Item key={kpi._id} >
                                    {kpi.title}
                                </Menu.Item>
                            ))}
                        </Menu.SubMenu>
                    ))}
                </Menu>
                <Spin spinning={isEnquiring} tip="Loading" fullscreen delay={1000}></Spin>
            </Sider>
            <Content style={{ padding: '0 24px', minHeight: 280, background: colorBgContainer }}>
                {selectedKPI ? (
                    <KpiForm />
                ) : selectedCategory ? (
                    <div>
                        <h3>KPI Group: {selectedCategory.title}</h3>
                        <Button icon={<EditOutlined />} onClick={() => handleCategoryEdit(selectedCategory.id, selectedCategory.title)}>Edit KPI Group</Button>
                    </div>
                ) : (
                    <div>Select KPI to view/edit details.</div>
                )}
            </Content>
            {/* Category Modal */}
            <Modal
                title={categoryEditId ? 'Edit KPI Group' : 'Create KPI Group'}
                open={categoryModalOpen}
                onOk={handleCategorySave}
                onCancel={() => setCategoryModalOpen(false)}
                okText="Save"
            >
                <Input
                    placeholder="KPI Group Name"
                    value={categoryName}
                    onChange={e => setCategoryName(e.target.value)}
                />
                <Input.TextArea
                    placeholder="Description (optional)"
                    style={{ marginTop: 8 }}
                    value={categoryDescription}
                    onChange={e => setCategoryDescription(e.target.value)}
                />
            </Modal>
            {/* KPI Modal */}
            <Modal
                title={kpiEditId ? 'Edit KPI' : 'Add KPI'}
                open={kpiModalOpen}
                onOk={handleKPISave}
                onCancel={() => setKPIModalOpen(false)}
                okText="Save"
            >
                <Input
                    placeholder="KPI Name"
                    value={kpiName}
                    onChange={e => setKPIName(e.target.value)}
                />
            </Modal>
        </Layout>
    );
};

export default KPI;
